import { useMemo } from "react"
import { useOutletContext } from "react-router-dom"

import { validateDecimalValue } from "@components/Amount"

const TIER_COLUMN_PREFIX = "Target"
const TARGET_INVESTMENT_ID_COLUMN = "Target Investment ID"
const SOURCE_INVESTMENT_ID_COLUMN = "Source Investment ID"

const normalizeAmountValue = value =>
  value
    .replace('$', '')
    .replaceAll(',', '')

const useTemplateParser = (setErrors, getItems, targetProjectId) => {
  const { getProject } = useOutletContext()

  const targetProject = getProject(targetProjectId)

  const targetProjectTierIds = useMemo(() =>
    targetProject
      ? targetProject.tiers.map(({ name }) => name)
      : []
  , [ targetProject ])

  const parseRow = row => {
    const item = {
      investmentId: row[TARGET_INVESTMENT_ID_COLUMN] || row[SOURCE_INVESTMENT_ID_COLUMN],
      sourceInvestmentId: row[SOURCE_INVESTMENT_ID_COLUMN],
    }

    for (const tierId of targetProjectTierIds) {
      const TIER_COLUMN = `${TIER_COLUMN_PREFIX} ${tierId}`
      item[tierId] = row[TIER_COLUMN]
    }

    return item
  }

  const validateRow = (item, rowIndex) => {
    const { investmentId } = item

    const investments = getItems()

    const getInvestment = id => investments.find(investment => investment.id === id)

    const isInvestmentFound = !!getInvestment(investmentId)

    if (!isInvestmentFound) {
      setErrors((prevItems = []) => [
        ...prevItems,
        {
          index: rowIndex + 1,
          reason: `Includes invalid investment ID: ${investmentId}`
        }
      ])

      return false
    }

    for (const tierId of targetProjectTierIds) {
      const value = item[tierId]

      if (value) {
        const normalizedValue = normalizeAmountValue(value)
        const isValid = validateDecimalValue(normalizedValue)

        if (!isValid) {
          const TIER_COLUMN = `${TIER_COLUMN_PREFIX} ${tierId}`

          setErrors((prevItems = []) => [
            ...prevItems,
            {
              index: rowIndex + 1,
              reason: `Column "${TIER_COLUMN}" value has invalid format: ${value}`
            }
          ])

          return false
        }
      }
    }

    return true
  }

  const buildRow = item => {
    for (const tierId of targetProjectTierIds) {
      const value = item[tierId]

      if (value) {
        const normalizedValue = normalizeAmountValue(value)

        const centsValue = normalizedValue.includes('.')
          ? normalizedValue.replace('.', '')
          : `${normalizedValue}00`

        item[tierId] = Number(centsValue)
      }

      if (!value) {
        delete item[tierId]
      }
    }

    return item
  }

  return {
    buildRow,
    parseRow,
    validateRow,
  }
}

export default useTemplateParser
